<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanCard from './KanbanCard.vue';
import CardDialog from './CardDialog.vue';
import KanbanColumnSettings from './KanbanColumnSettings.vue';

const props = defineProps({
  boardId: {
    type: Number,
    required: true,
  },
  column: {
    type: Object,
    required: true,
  },
  cards: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['editColumn', 'deleteColumn']);

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const localCards = ref(props.cards);

const currentPage = ref(1);
const isFetchingMore = ref(false);

const hasMoreCards = computed(
  () => props.cards.length < props.column.cards_count
);

const loadMore = async () => {
  isFetchingMore.value = true;
  try {
    currentPage.value += 1;
    await store.dispatch('kanban/getMoreCards', {
      boardId: props.boardId,
      columnId: props.column.id,
      page: currentPage.value,
    });
  } catch (error) {
    useAlert(t('KANBAN.CARDS.API.LOAD_MORE_ERROR'));
    currentPage.value -= 1;
  } finally {
    isFetchingMore.value = false;
  }
};

watch(
  () => props.column.id,
  () => {
    currentPage.value = 1;
  }
);

watch(
  () => props.cards,
  newCards => {
    localCards.value = newCards;
  },
  { deep: true }
);

let cardDragStart = [];

const persistCardsOrder = async orderedIds => {
  try {
    await store.dispatch('kanban/reorderCards', {
      boardId: props.boardId,
      kanbanColumnId: props.column.id,
      orderedIds,
    });
  } catch (error) {
    useAlert(t('KANBAN.CARDS.API.REORDER_ERROR'));
  }
};

const onCardDragStart = () => {
  cardDragStart = localCards.value.map(card => card.id);
};

const onCardMove = evt => {
  const isSameList = evt.from === evt.to;
  if (isSameList || props.column.position === 0) return true;
  if (evt.draggedContext.element.eligible !== false) return true;
  return false;
};

const onCardDragEnd = async () => {
  const orderedIds = localCards.value.map(card => card.id);
  const isNoOp = orderedIds.every((id, index) => id === cardDragStart[index]);
  if (isNoOp) {
    return;
  }
  await persistCardsOrder(orderedIds);
};

const onCardDragAdd = () => {
  persistCardsOrder(localCards.value.map(card => card.id));
};

onMounted(() => {
  if (!store.getters['agents/getVerifiedAgents']?.length) {
    store.dispatch('agents/get');
  }
});

const assigneeForCard = card => {
  if (!card.assignee_id) return null;
  const agent = store.getters['agents/getAgentById'](card.assignee_id);
  return agent?.id ? agent : null;
};

const cardDialogRef = ref(null);
const editingCard = ref(null);
const cardToDelete = ref(null);
const showCardDeletePopup = ref(false);
const isSavingCard = ref(false);
const settingsDialogRef = ref(null);

const openSettingsDialog = () => {
  settingsDialogRef.value?.open();
};

const openCreateCardDialog = () => {
  editingCard.value = null;
  cardDialogRef.value?.open();
};

const openEditCardDialog = card => {
  editingCard.value = card;
  cardDialogRef.value?.open();
};

const openCardDeletePopup = card => {
  cardToDelete.value = card;
  showCardDeletePopup.value = true;
};

const closeCardDeletePopup = () => {
  showCardDeletePopup.value = false;
};

const saveCard = async ({
  title,
  description,
  assignee_id,
  conversation_id,
}) => {
  isSavingCard.value = true;
  try {
    if (editingCard.value) {
      await store.dispatch('kanban/updateCard', {
        boardId: props.boardId,
        cardId: editingCard.value.id,
        title,
        description,
        assignee_id,
        conversation_id,
      });
      useAlert(t('KANBAN.CARDS.API.SUCCESS_UPDATE'));
    } else {
      await store.dispatch('kanban/createCard', {
        boardId: props.boardId,
        kanban_column_id: props.column.id,
        title,
        description,
        position: props.cards.length,
        assignee_id,
        conversation_id,
      });
      useAlert(t('KANBAN.CARDS.API.SUCCESS_CREATE'));
    }
    cardDialogRef.value?.close();
  } catch (error) {
    useAlert(t('KANBAN.CARDS.API.ERROR_MESSAGE'));
  } finally {
    isSavingCard.value = false;
  }
};

const confirmCardDeletion = async () => {
  try {
    await store.dispatch('kanban/deleteCard', {
      boardId: props.boardId,
      cardId: cardToDelete.value.id,
    });
    useAlert(t('KANBAN.CARDS.API.SUCCESS_DELETE'));
    closeCardDeletePopup();
  } catch (error) {
    useAlert(t('KANBAN.CARDS.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <section
    class="flex flex-col w-72 h-full min-h-0 rounded-xl border border-n-weak bg-n-solid-1"
  >
    <header class="flex items-center justify-between gap-2 px-3 py-2.5">
      <div class="flex items-center gap-2 min-w-0">
        <h3 class="text-sm font-semibold text-n-slate-12 truncate">
          {{ column.name }}
        </h3>
        <span class="text-xs text-n-slate-10">
          {{ column.cards_count || 0 }} · {{ column.cards_percentage || 0 }}%
        </span>
      </div>
      <div v-if="isAdmin" class="flex items-center gap-0.5">
        <Button
          size="sm"
          variant="ghost"
          class="!px-1.5"
          icon="i-lucide-settings"
          :aria-label="t('KANBAN.COLUMNS.SETTINGS_TITLE')"
          :title="t('KANBAN.COLUMNS.SETTINGS_TITLE')"
          @click="openSettingsDialog"
        />
        <Button
          size="sm"
          variant="ghost"
          class="!px-1.5"
          icon="i-lucide-pencil-line"
          :aria-label="t('KANBAN.COLUMNS.EDIT_TITLE')"
          :title="t('KANBAN.COLUMNS.EDIT_TITLE')"
          @click="emit('editColumn', column)"
        />
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          class="!px-1.5"
          icon="i-lucide-trash"
          :aria-label="t('KANBAN.COLUMNS.DELETE_TITLE')"
          :title="t('KANBAN.COLUMNS.DELETE_TITLE')"
          @click="emit('deleteColumn', column)"
        />
      </div>
    </header>
    <div
      class="flex flex-col flex-1 min-h-0 gap-2 p-2 overflow-y-auto"
      data-testid="kanban-column-cards"
    >
      <Draggable
        v-model="localCards"
        item-key="id"
        group="kanban-cards"
        tag="ul"
        :disabled="!isAdmin"
        class="flex flex-col gap-2"
        ghost-class="opacity-40"
        @start="onCardDragStart"
        @move="onCardMove"
        @end="onCardDragEnd"
        @add="onCardDragAdd"
      >
        <template #item="{ element }">
          <li class="list-none">
            <KanbanCard
              :card="element"
              :assignee="assigneeForCard(element)"
              @click="openEditCardDialog(element)"
              @delete="openCardDeletePopup(element)"
            />
          </li>
        </template>
      </Draggable>
      <Button
        v-if="hasMoreCards"
        data-testid="load-more-button"
        variant="clear"
        color="blue"
        class="w-full justify-center !py-1 text-xs animate-pulse"
        :label="t('KANBAN.CARDS.LOAD_MORE')"
        :is-loading="isFetchingMore"
        @click="loadMore"
      />
      <Button
        variant="outline"
        color="slate"
        class="w-full"
        icon="i-lucide-plus"
        :label="t('KANBAN.CARDS.ADD_BUTTON')"
        @click="openCreateCardDialog"
      />
    </div>

    <CardDialog
      ref="cardDialogRef"
      :card="editingCard"
      :is-saving="isSavingCard"
      @submit="saveCard"
    />

    <KanbanColumnSettings
      ref="settingsDialogRef"
      :board-id="props.boardId"
      :column="props.column"
    />

    <woot-delete-modal
      v-model:show="showCardDeletePopup"
      :on-close="closeCardDeletePopup"
      :on-confirm="confirmCardDeletion"
      :title="t('KANBAN.CARDS.DELETE_TITLE')"
      :message="t('KANBAN.CARDS.DELETE_CONFIRM_MESSAGE')"
      :confirm-text="t('KANBAN.CARDS.CONFIRM_DELETE')"
      :reject-text="t('KANBAN.CARDS.REJECT_DELETE')"
    />
  </section>
</template>
